/**
 * Easter brunch menus — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easterBrunchMenusPack: ToilePack = {
    slug: 'easter-brunch-menus',
    category: 'season',
    name: 'Easter brunch menus',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'brunch', label: 'Brunch menu', subject: 'A vertical menu card lettered "EASTER BRUNCH" with headings "To Start", "Mains", "Sweet" and blank lines, a tulip border'},
        {key: 'lunch', label: 'Lunch menu', subject: 'A vertical menu card lettered "EASTER LUNCH" with a long blank ruled list and a small lamb motif'},
        {key: 'kids', label: "Kids' menu", subject: 'A vertical kids\' menu lettered "KIDS MENU" with three blank boxes and cartoon egg motifs, a colouring border'},
    ],
    styles: [
        {key: 'pastel', label: 'Pastel', style: 'pastel easter printable, egg and floral borders, soft rounded lettering, print-ready', palette: 'Soft pink, mint, lilac and butter yellow', framing: 'Laid out on a vertical page with an even white margin'},
        {key: 'vintage', label: 'Vintage', style: 'vintage easter printable, engraved floral borders, elegant serif lettering, print-ready', palette: 'Rose, sage, cream and a little gold', framing: 'Laid out on a cream page with an ornate border and a margin'},
        {key: 'kids', label: 'Kids', style: "kids' easter printable, bright cartoon bunnies and chicks, rounded lettering, print-ready", palette: 'Bright yellow, sky blue, grass green and pink', framing: 'Laid out on a vertical page with a playful border and a white margin'},
    ],
    directions: ['for a mimosa bar', 'as a two-sided card', 'with a coloring page back'],
    directionHint: 'try: for a mimosa bar',
    seo: {
        title: "Easter menu: brunch, lunch and kids' table",
        metaDescription: "Nine Easter menus — a brunch menu, a lunch menu, a kids' menu — courses lettered, dishes left blank, pastel, vintage or kids' style.",
        h1: "Easter menu: brunch, lunch and kids' table in three styles",
        tagline: 'Nine menus, three table formats, three styles each — course headers set, dishes left for you to fill in.',
        intro: "Three Easter menus for three kinds of table: a brunch spread, a sit-down lunch, a kids' menu. Each names its courses and leaves the dish lines blank to write in your own spread. Drawn in a soft pastel look, a vintage two-tone finish, or a bright kids' style. Set at each place or pin above the buffet.",
        useCases: [
            'a place setting or table centerpiece',
            'a buffet table sign listing the spread',
            "a kids' table activity that doubles as a menu",
            "a keepsake of the year's Easter dishes",
        ],
        faq: [
            {q: 'Are the dishes already printed on the menu?', a: "No — the course headers are set, but the dish lines are blank so you write in whatever's actually on the table."},
            {q: 'What size are the menus?', a: 'Each is drawn at a standard menu-card proportion, sized for a place setting or a small buffet sign.'},
            {q: 'Can I change the course headers?', a: "Yes — type a direction like 'add a dessert course' and the header set redraws to match."},
            {q: "Is the kids' menu meant to be filled in by children?", a: "It can be — the kids' style uses larger lines and bright icons, well suited to a child writing or drawing what they're eating."},
        ],
    },
}
